/**
 * girlcave — Safari chrome tint sampler.
 *
 * Safari 26 does not composite the page behind its status bar / bottom bar at
 * rest. It scans for position: fixed|sticky elements near the viewport edges
 * and reads their background-COLOR to tint that chrome, falling back to the
 * root color when there is no candidate (theme-color is ignored entirely).
 * .gc-backdrop is not a candidate — it carries an image and no color, so
 * Safari reads it as transparent.
 *
 * So snippets/gc-backdrop.liquid ships one thin fixed strip per edge, and this
 * script colors each from the ACTIVE plate: it reads .gc-backdrop's rendered
 * rect, replays the `cover` + `center` mapping, and averages the artwork just
 * PAST each screen edge — above the top, below the bottom. That is what the
 * bar sits on the far side of, so it is what the bar should continue, and on
 * a scene plate it falls out as sky at the top and ground at the bottom.
 * Two colors is the entire point — the plate's ends differ, and every
 * single-color attempt fixed one end by breaking the other (user reports
 * 2026-08-30: dark olive above the sky, then blue below the grass).
 *
 * --gc-canvas-fill keeps the root/canvas colored for the pre-paint state, deep
 * rubber-band overscroll, and any browser that finds no strip. It takes the
 * top-edge sample.
 *
 * Follows client-set per-page art automatically — the resolved plate URLs ride
 * on data-gc-plate-mobile/desktop (Theme settings → girlcave pages).
 */
(function () {
  'use strict';

  var backdrop = document.querySelector('.gc-backdrop');
  if (!backdrop) return;

  var mq = window.matchMedia('(min-width: 768px)');

  /* How far PAST each screen edge to average, in rendered plate px. Deep
     enough to be a real read of the artwork rather than one row, shallow
     enough to stay local to that edge. */
  var BAND = 120;
  /* Sampling grid. Height drives how finely the strip can be addressed. */
  var GRID_W = 16;
  var GRID_H = 128;

  function rgbString(r, g, b) {
    return 'rgb(' + Math.round(r) + ', ' + Math.round(g) + ', ' + Math.round(b) + ')';
  }

  function bandAverage(data, width, y0, y1) {
    var r = 0, g = 0, b = 0, n = 0;
    for (var y = y0; y < y1; y++) {
      for (var x = 0; x < width; x++) {
        var i = (y * width + x) * 4;
        r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
      }
    }
    return [r / n, g / n, b / n];
  }

  /* Where the screen's BOTTOM edge is. On iOS the layout viewport can be the
     small one (toolbar expanded) while Safari's bottom bar sits at the bottom
     of the large one — clientHeight would sample too far up the plate and pick
     up the horizon instead of the ground. Measure 100lvh, fall back to
     clientHeight where lvh is unsupported. */
  function screenHeight() {
    var probe = document.createElement('div');
    /* display:block is load-bearing: base.css hides `div:empty`, and a probe
       with no layout box measures 0 — the fallback would silently win on
       every device. Inline style outranks the stylesheet rule. */
    probe.style.cssText =
      'display:block;position:fixed;top:0;left:0;width:0;height:100lvh;' +
      'visibility:hidden;pointer-events:none';
    document.body.appendChild(probe);
    var height = probe.getBoundingClientRect().height;
    probe.parentNode.removeChild(probe);
    return height || document.documentElement.clientHeight;
  }

  /* Which slices of the plate to read, as fractions of the image's own height.
     .gc-backdrop is `background-size: cover` + `background-position: center`
     inside the overscanned box, so: scale the image to cover the box, centre
     it, and read off where the screen's edges fall. Measuring the rendered
     element rather than recomputing the CSS keeps this correct whatever the
     overscan resolves to.

     Each band is taken OUTWARD — the top strip reads the artwork ABOVE the
     screen's top edge, the bottom strip the artwork BELOW its bottom edge.
     That is precisely what the bar should be showing: the continuation of the
     image past the edge it sits on. It is also what makes the two ends read
     as the scene does — sky above, ground below — where sampling inward from
     the bottom edge lands on the horizon haze on a tall viewport. */
  function edgeBands(img) {
    var box = backdrop.getBoundingClientRect();
    if (!box.height || !img.naturalHeight) return null;

    var scale = Math.max(box.width / img.naturalWidth, box.height / img.naturalHeight);
    var drawnHeight = img.naturalHeight * scale;
    var imageTop = box.top + (box.height - drawnHeight) / 2;
    var bottomEdge = screenHeight();

    /* Clamping keeps a band inside the image: a request that runs past an end
       settles on that end, which is the right answer anyway — the artwork the
       plate finishes on. */
    function band(fromY, toY) {
      var start = (fromY - imageTop) / drawnHeight;
      var span = Math.max((toY - fromY) / drawnHeight, 1 / GRID_H);
      start = Math.min(Math.max(start, 0), 1 - span);
      return { start: start, span: span };
    }

    return {
      top: band(-BAND, 0),
      bottom: band(bottomEdge, bottomEdge + BAND)
    };
  }

  function average(data, band) {
    var y0 = Math.floor(band.start * GRID_H);
    var y1 = Math.min(GRID_H, Math.max(y0 + 1, Math.ceil((band.start + band.span) * GRID_H)));
    return bandAverage(data, GRID_W, y0, y1);
  }

  function sample(url) {
    if (!url) return;
    var img = new Image();
    img.decoding = 'async';
    /* Shopify CDN serves ACAO:* — with CORS the canvas stays untainted and
       getImageData works. Without it the read throws and the strips stay
       transparent, which degrades to the root fill rather than a wrong band. */
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        var bands = edgeBands(img);
        if (!bands) return;

        var canvas = document.createElement('canvas');
        canvas.width = GRID_W;
        canvas.height = GRID_H;
        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, GRID_W, GRID_H);
        var data = ctx.getImageData(0, 0, GRID_W, GRID_H).data;

        var top = average(data, bands.top);
        var bottom = average(data, bands.bottom);
        var root = document.documentElement.style;

        root.setProperty('--gc-chrome-tint-top', rgbString(top[0], top[1], top[2]));
        root.setProperty('--gc-chrome-tint-bottom', rgbString(bottom[0], bottom[1], bottom[2]));
        root.setProperty('--gc-canvas-fill', rgbString(top[0], top[1], top[2]));
      } catch (error) {
        /* Tainted canvas or decode failure — keep the pre-JS defaults. */
      }
    };
    img.src = url;
  }

  function update() {
    sample(backdrop.getAttribute(mq.matches ? 'data-gc-plate-desktop' : 'data-gc-plate-mobile'));
  }

  if (mq.addEventListener) mq.addEventListener('change', update);
  else mq.addListener(update);

  /* The box — and so both sampled rows — is a function of the viewport, and a
     rotation can change it without crossing the 768px breakpoint. The plate is
     cached by then, so re-reading it is cheap. */
  var pending;
  window.addEventListener('resize', function () {
    clearTimeout(pending);
    pending = setTimeout(update, 200);
  });

  /* Soft page changes (assets/gc-navigation.js) carry the next page's plate
     URLs over onto this element — the drop page has its own. */
  document.addEventListener('gc:page-load', update);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', update);
  else update();
})();
